import { useEffect, useState, useSyncExternalStore } from 'react';
import { ApiError, UnreadableReply, api, get } from '../api';
import { viewAsId } from '../viewAs';

/**
 * Working with a poor signal. Reads are saved on the phone as they arrive and
 * shown (marked as saved) when the network is out. Writes that cannot be sent
 * wait in a queue, in order, and go as soon as the phone is back online. The
 * server treats every one of these writes as safe to repeat, so a send that
 * half-worked is simply sent again.
 *
 * Everything is kept per signed-in person, so a phone shared between two
 * contractors never sends one's work under the other's name.
 */

export interface Queued {
  key: string;
  owner: number;
  method: 'POST' | 'DELETE';
  url: string;
  body?: unknown;
  label: string;
}
export interface Outcome {
  key: string;
  label: string;
  ok: boolean;
  message: string;
}

/** How long to wait for an answer before treating a weak signal as none. */
export const PORTAL_WAIT = 10_000;

const QUEUE = 'teknika_portal_queue';
const OUTCOMES = 'teknika_portal_outcomes';
const CACHE = 'teknika_portal_cache:';

let owner = 0;
/**
 * Called while the shell renders, so the store's listeners hear about it just after,
 * never during a render (React warns when one component updates another mid-render).
 */
export function setOwner(id: number) {
  if (owner === id) return;
  owner = id;
  queueMicrotask(notify);
}

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}
function write(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage full or blocked: the app still works while online.
  }
}

// ---- A tiny store, so any screen can show what is waiting ----
interface Snapshot {
  waiting: number;
  outcomes: Outcome[];
  /** Goes up every time the queue sends something, so screens know to reload. */
  synced: number;
}
let synced = 0;
let snapshot: Snapshot = { waiting: 0, outcomes: [], synced: 0 };
const listeners = new Set<() => void>();

function notify() {
  const waiting = read<Queued[]>(QUEUE, []).filter((q) => q.owner === owner).length;
  const outcomes = read<Outcome[]>(OUTCOMES, []);
  snapshot = { waiting, outcomes, synced };
  listeners.forEach((l) => l());
}

export function useOutbox(): Snapshot {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => snapshot,
  );
}

/** Tell every screen on show to load again: something it shows has changed. */
export function refreshAll() {
  synced++;
  notify();
}

export function dismissOutcomes() {
  write(OUTCOMES, []);
  notify();
}

/**
 * True when the failure is the network, not an answer from the server. A reply the app
 * cannot read (a wifi sign-in page, a proxy's error page) counts as no signal too.
 */
export const isOffline = (e: unknown) => !(e instanceof ApiError) || e instanceof UnreadableReply;

/** The server itself failed (not a refusal): the saved copy is still the best thing to show. */
const serverTrouble = (e: unknown) => e instanceof ApiError && e.status >= 500;

function addOutcome(o: Outcome) {
  write(OUTCOMES, [...read<Outcome[]>(OUTCOMES, []), o].slice(-20));
}

let counter = 0;
const newKey = () => `${Date.now().toString(36)}-${(counter++).toString(36)}`;

/** A random key for a note, so a resend is stored once. */
export const noteRef = () =>
  `n-${newKey()}-${Math.random().toString(36).slice(2, 10)}`.slice(0, 64);

/**
 * Do something now if there is a signal, otherwise queue it. Rule refusals
 * (someone else took it, the visit was cancelled) are thrown so the screen can
 * say so; only a failure to reach the server queues the action.
 */
export async function act(
  label: string,
  method: 'POST' | 'DELETE',
  url: string,
  body?: unknown,
): Promise<'done' | 'queued'> {
  // Viewing as someone: send it now or fail, never queue it on this device.
  if (viewAsId() !== null) {
    await api(method, url, body);
    return 'done';
  }
  const sent = () => {
    // Every screen reloads, so a list beside the visit shows the change straight away.
    refreshAll();
    return 'done' as const;
  };
  // Keep order: nothing jumps the queue while earlier work is still waiting.
  const backlog = read<Queued[]>(QUEUE, []).some((q) => q.owner === owner);
  if (!backlog && navigator.onLine !== false) {
    try {
      await api(method, url, body, PORTAL_WAIT);
      return sent();
    } catch (e) {
      if (!isOffline(e)) throw e;
    }
  }
  write(QUEUE, [...read<Queued[]>(QUEUE, []), { key: newKey(), owner, method, url, body, label }]);
  notify();
  return 'queued';
}

let flushing = false;
/** Send what is waiting, oldest first. Stops at the first sign that the network or the sign-in is gone. */
export async function flush() {
  if (flushing || owner === 0 || viewAsId() !== null) return;
  flushing = true;
  try {
    for (;;) {
      const item = read<Queued[]>(QUEUE, []).find((q) => q.owner === owner);
      if (!item) break;
      const drop = () =>
        write(
          QUEUE,
          read<Queued[]>(QUEUE, []).filter((q) => q.key !== item.key),
        );
      try {
        await api(item.method, item.url, item.body, PORTAL_WAIT);
        drop();
        addOutcome({ key: item.key, label: item.label, ok: true, message: 'Sent.' });
      } catch (e) {
        if (isOffline(e)) break;
        const status = (e as ApiError).status;
        if (status === 401 || status >= 500) break; // sign in again, or try again later
        drop();
        addOutcome({
          key: item.key,
          label: item.label,
          ok: false,
          message: (e as ApiError).message,
        });
      }
      synced++;
      notify();
    }
  } finally {
    flushing = false;
    notify();
  }
}

/** Send the queue now, and again whenever the phone comes back online. */
export function useAutoFlush(active: boolean) {
  useEffect(() => {
    if (!active) return;
    void flush();
    const go = () => void flush();
    window.addEventListener('online', go);
    const t = window.setInterval(go, 30_000);
    return () => {
      window.removeEventListener('online', go);
      window.clearInterval(t);
    };
  }, [active]);
}

// ---- Saved reads ----

interface SavedCopy<T> {
  v: 1;
  /** When it was fetched, so a stale screen can say how stale. */
  at: string;
  data: T;
}
export interface SavedRead<T> {
  data: T;
  saved: boolean;
  /** When the copy shown was fetched (ISO), if it is a saved one and the phone knows. */
  savedAt: string;
  /** Why the saved copy is showing: no signal, or the server failed. '' while still asking. */
  why: 'offline' | 'server' | '';
}

function readCopy<T>(url: string): { data: T; at: string } | null {
  const raw = read<SavedCopy<T> | T | null>(`${CACHE}${owner}:${url}`, null);
  if (raw === null) return null;
  // Copies saved before they carried a time are the data itself.
  const c = raw as Partial<SavedCopy<T>>;
  return c.v === 1 && 'data' in c
    ? { data: c.data as T, at: c.at ?? '' }
    : { data: raw as T, at: '' };
}

/**
 * Get from the server and keep a copy. With no signal, or when the server fails, return
 * the last copy and say so: a stale schedule beats a dead end for someone at a customer's door.
 */
export async function savedGet<T>(url: string): Promise<SavedRead<T>> {
  // Viewing as someone: their data is shown, never kept on this device.
  if (viewAsId() !== null) return { data: await get<T>(url), saved: false, savedAt: '', why: '' };
  try {
    const data = await get<T>(url, PORTAL_WAIT);
    write(`${CACHE}${owner}:${url}`, { v: 1, at: new Date().toISOString(), data });
    return { data, saved: false, savedAt: '', why: '' };
  } catch (e) {
    const offline = isOffline(e);
    if (offline || serverTrouble(e)) {
      const copy = readCopy<T>(url);
      if (copy)
        return {
          data: copy.data,
          saved: true,
          savedAt: copy.at,
          why: offline ? 'offline' : 'server',
        };
    }
    throw e;
  }
}

/** Forget every saved read: used on sign-out, so a phone does not keep someone's addresses. */
export function forgetSaved() {
  try {
    for (const k of Object.keys(localStorage)) {
      if (k.startsWith(CACHE)) localStorage.removeItem(k);
    }
  } catch {
    // Nothing to forget.
  }
}

/** Everything a screen needs to explain a load that did not work. */
export interface Failure {
  message: string;
  /** 0 when the server was never reached. */
  status: number;
  requestId: string;
  offline: boolean;
  url: string;
}

export interface PortalLoad<T> {
  data: T | null;
  saved: boolean;
  savedAt: string;
  why: SavedRead<T>['why'];
  /** The failure's message, or ''. Kept for screens that only need the words. */
  error: string;
  failure: Failure | null;
  loading: boolean;
  setData(next: T): void;
  /** Ask again now. */
  retry(): void;
}

const NO_SIGNAL = 'No signal, and nothing saved on this phone to show yet.';

export function failureOf(e: unknown, url: string): Failure {
  const offline = isOffline(e);
  const api = e instanceof ApiError ? e : null;
  return {
    message: offline ? NO_SIGNAL : (api?.message ?? 'Something went wrong.'),
    status: offline ? 0 : (api?.status ?? 0),
    requestId: api?.requestId ?? '',
    offline,
    url,
  };
}

type LoadState<T> = Omit<PortalLoad<T>, 'setData' | 'retry' | 'error'>;

/** Where a screen starts: what this phone saved last time, if anything, while it asks again. */
function startFrom<T>(url: string): LoadState<T> {
  const copy = viewAsId() === null ? readCopy<T>(url) : null;
  return {
    data: copy?.data ?? null,
    saved: copy !== null,
    savedAt: copy?.at ?? '',
    why: '',
    failure: null,
    loading: true,
  };
}

/**
 * Load a portal URL, reloading after the queue sends something and when the signal comes back.
 * It opens on the saved copy, so a weak signal never stands between someone and their gate code.
 */
export function usePortalLoad<T>(url: string): PortalLoad<T> {
  const { synced: version } = useOutbox();
  const [attempt, setAttempt] = useState(0);
  const [state, set] = useState<LoadState<T>>(() => startFrom<T>(url));
  useEffect(() => {
    let live = true;
    set((s) => ({ ...s, loading: true }));
    savedGet<T>(url)
      .then(
        (r) =>
          live &&
          set({
            data: r.data,
            saved: r.saved,
            savedAt: r.savedAt,
            why: r.why,
            failure: null,
            loading: false,
          }),
      )
      .catch((e) => {
        if (live) {
          set({
            data: null,
            saved: false,
            savedAt: '',
            why: '',
            failure: failureOf(e, url),
            loading: false,
          });
        }
      });
    return () => {
      live = false;
    };
  }, [url, version, attempt]);
  // A screen showing a saved copy or a failure tries again as soon as the phone is back online.
  const stuck = state.saved || state.failure !== null;
  useEffect(() => {
    if (!stuck) return;
    const again = () => setAttempt((n) => n + 1);
    window.addEventListener('online', again);
    return () => window.removeEventListener('online', again);
  }, [stuck]);
  return {
    ...state,
    error: state.failure?.message ?? '',
    setData: (data) => set((s) => ({ ...s, data })),
    retry: () => setAttempt((n) => n + 1),
  };
}

/** Whether the phone thinks it has a connection. */
export function useOnline() {
  const [on, setOn] = useState(navigator.onLine);
  useEffect(() => {
    const up = () => setOn(true);
    const down = () => setOn(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);
  return on;
}
